import { Component, EventEmitter, Input, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { navigateFromNotification } from '../../common/notification-navigation.util';
import { ButtonModule } from 'primeng/button';
import { INotification } from '../../models/notification';

@Component({
  selector: 'app-notification-inbox-panel',
  standalone: true,
  imports: [CommonModule, ButtonModule],
  template: `
    <div class="notification-panel">
      <div class="notification-panel-header">
        <div class="notification-panel-heading">
          <span class="notification-panel-title">Notifications</span>
          @if (unreadCount > 0) {
            <span class="notification-panel-count">{{ unreadCount }} unread</span>
          }
        </div>
        <button
          type="button"
          class="notification-panel-action"
          (click)="markAllRead.emit()"
          [disabled]="unreadCount === 0"
        >
          Mark all read
        </button>
      </div>

      @if (loading) {
        <div class="notification-panel-empty">
          <i class="pi pi-spin pi-spinner"></i>
          <span>Loading notifications...</span>
        </div>
      } @else if (!notifications.length) {
        <div class="notification-panel-empty">
          <i class="pi pi-inbox"></i>
          <span>No notifications yet</span>
        </div>
      } @else {
        <ul class="notification-list">
          @for (item of notifications; track item.userNotificationId) {
            <li
              class="notification-item"
              [class.notification-item-unread]="!item.isRead"
              (click)="onItemClick(item)"
            >
              <span class="notification-item-indicator" [class.notification-item-indicator-unread]="!item.isRead"></span>
              <div class="notification-item-content">
                <div class="notification-item-title">{{ item.title }}</div>
                <div class="notification-item-message">{{ item.message }}</div>
                <div class="notification-item-meta">{{ item.createdAtUtc | date: 'medium' }}</div>
              </div>
            </li>
          }
        </ul>
      }
    </div>
  `,
  styles: [
    `
      .notification-panel {
        width: min(22rem, calc(100vw - 2rem));
        max-height: min(24rem, calc(100vh - 8rem));
        display: flex;
        flex-direction: column;
        overflow: hidden;
      }

      .notification-panel-header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 0.75rem;
        padding: 0.875rem 1rem;
        border-bottom: 1px solid var(--surface-border);
        background: var(--surface-card);
      }

      .notification-panel-heading {
        display: flex;
        flex-direction: column;
        gap: 0.15rem;
        min-width: 0;
      }

      .notification-panel-title {
        font-size: 0.95rem;
        font-weight: 600;
        color: var(--text-color);
      }

      .notification-panel-count {
        font-size: 0.75rem;
        color: var(--primary-color);
        font-weight: 500;
      }

      .notification-panel-action {
        flex-shrink: 0;
        font-size: 0.8rem;
        color: var(--primary-color);
        cursor: pointer;
        border: none;
        background: transparent;
        padding: 0;
      }

      .notification-panel-action:disabled {
        opacity: 0.45;
        cursor: default;
      }

      .notification-panel-empty {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        gap: 0.5rem;
        padding: 2rem 1rem;
        text-align: center;
        color: var(--text-color-secondary);
        font-size: 0.875rem;
      }

      .notification-panel-empty i {
        font-size: 1.35rem;
        opacity: 0.7;
      }

      .notification-list {
        list-style: none;
        margin: 0;
        padding: 0.25rem 0;
        overflow-y: auto;
        max-height: 20rem;
      }

      .notification-item {
        display: flex;
        align-items: flex-start;
        gap: 0.65rem;
        padding: 0.8rem 1rem;
        border-bottom: 1px solid var(--surface-border);
        cursor: pointer;
        transition: background-color 0.15s ease;
        animation: notification-item-in 200ms var(--ease-out) both;
      }

      @keyframes notification-item-in {
        from {
          opacity: 0;
          transform: translateY(-4px);
        }
        to {
          opacity: 1;
          transform: translateY(0);
        }
      }

      @media (prefers-reduced-motion: reduce) {
        .notification-item {
          animation: none;
        }
      }

      .notification-item:last-child {
        border-bottom: none;
      }

      .notification-item:hover {
        background: var(--surface-hover);
      }

      .notification-item-unread {
        background: color-mix(in srgb, var(--primary-color) 6%, var(--surface-card));
        border-left: 3px solid var(--primary-color);
        padding-left: calc(1rem - 3px);
      }

      .notification-item-indicator {
        width: 0.5rem;
        height: 0.5rem;
        border-radius: 50%;
        margin-top: 0.35rem;
        flex-shrink: 0;
        background: transparent;
      }

      .notification-item-indicator-unread {
        background: var(--primary-color);
      }

      .notification-item-content {
        min-width: 0;
        flex: 1;
      }

      .notification-item-title {
        font-weight: 600;
        font-size: 0.875rem;
        margin-bottom: 0.2rem;
        color: var(--text-color);
      }

      .notification-item-message {
        font-size: 0.8125rem;
        line-height: 1.4;
        color: var(--text-color-secondary);
      }

      .notification-item-meta {
        margin-top: 0.4rem;
        font-size: 0.75rem;
        color: var(--text-color-secondary);
      }
    `
  ]
})
export class NotificationInboxPanelComponent {
  @Input() notifications: INotification[] = [];
  @Input() unreadCount = 0;
  @Input() loading = false;

  @Output() markAllRead = new EventEmitter<void>();
  @Output() itemSelected = new EventEmitter<INotification>();

  constructor(private router: Router) {}

  onItemClick(item: INotification): void {
    this.itemSelected.emit(item);
    navigateFromNotification(this.router, item);
  }
}
